import React, { useState, useEffect, useCallback, useRef } from 'react';
import DashboardCard from '../../../shared/ui/cards/DashboardCard';
import {
  ledgerV2Api,
  type ExecutorBalanceV2Item,
} from '../../../api/ledgerV2Api';
import { paymentsV2Api } from '../../../api/paymentsV2Api';
import AccountingPayBottomSheet, { type AccountingPayTarget } from './AccountingPayBottomSheet';
import styles from './AccountingCard.module.css';

interface AccountingCardProps {
  siteId: number;
  onOpenExecutorBalance?: (siteExecutorId: number) => void;
}

const moneyOpts: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

function formatRub(n: number): string {
  return `${n.toLocaleString('ru-RU', moneyOpts)} ₽`;
}

/** Как в списке смен: вместо цифр при скрытых финансах */
const MASKED_AMOUNT = '*** ₽';

const AccountingCard: React.FC<AccountingCardProps> = ({ siteId, onOpenExecutorBalance }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [rows, setRows] = useState<ExecutorBalanceV2Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasFinanceAccess, setHasFinanceAccess] = useState(true);
  /** Глаз в шапке: показать все суммы и итог */
  const [allFinancialVisible, setAllFinancialVisible] = useState(false);
  /** Раскрытые по одной строке (как uncensoredSessionIds в карточке смен) */
  const [uncensoredExecutorIds, setUncensoredExecutorIds] = useState<Set<number>>(new Set());
  const [canEdit, setCanEdit] = useState(false);
  const [paySheetOpen, setPaySheetOpen] = useState(false);
  const [payTarget, setPayTarget] = useState<AccountingPayTarget | null>(null);
  /** Раскрытая панель действий по клику на баланс */
  const [expandedExecutorIds, setExpandedExecutorIds] = useState<Set<number>>(new Set());

  const wasOpenRef = useRef(false);
  const prevFullscreenRef = useRef(false);

  useEffect(() => {
    const isOpen = !isCollapsed || isFullscreenOpen;
    if (wasOpenRef.current && !isOpen) {
      setRows([]);
      setLoadError(null);
      setLoading(false);
      setAllFinancialVisible(false);
      setUncensoredExecutorIds(new Set());
      setExpandedExecutorIds(new Set());
    }
    wasOpenRef.current = isOpen;
  }, [isCollapsed, isFullscreenOpen]);

  useEffect(() => {
    if (prevFullscreenRef.current && !isFullscreenOpen) {
      setAllFinancialVisible(false);
      setUncensoredExecutorIds(new Set());
      setExpandedExecutorIds(new Set());
    }
    prevFullscreenRef.current = isFullscreenOpen;
  }, [isFullscreenOpen]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const data = await ledgerV2Api.getSiteExecutorBalances(siteId);
      setRows(data);
      setHasFinanceAccess(true);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        setHasFinanceAccess(false);
        setRows([]);
        setLoadError(null);
      } else {
        console.error('Accounting balances load error', err);
        setLoadError('Не удалось загрузить балансы');
        setRows([]);
      }
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    if (!isCollapsed || isFullscreenOpen) {
      loadData();
    }
  }, [isCollapsed, isFullscreenOpen, loadData]);

  useEffect(() => {
    if (!isCollapsed || isFullscreenOpen) {
      paymentsV2Api.checkEditPermission(siteId).then(setCanEdit).catch(() => setCanEdit(false));
    }
  }, [isCollapsed, isFullscreenOpen, siteId]);

  const handleOpenPay = useCallback((row: ExecutorBalanceV2Item, e: React.MouseEvent) => {
    e.stopPropagation();
    setPayTarget({
      siteExecutorId: row.siteExecutorId,
      executorDisplayName: row.executorDisplayName,
      balance: Number(row.balance) || 0,
    });
    setPaySheetOpen(true);
  }, []);

  const handleClosePaySheet = useCallback(() => {
    setPaySheetOpen(false);
    setPayTarget(null);
  }, []);

  const handleToggleAllFinancialVisible = useCallback(() => {
    setAllFinancialVisible((prev) => {
      if (prev) {
        setUncensoredExecutorIds(new Set());
      }
      return !prev;
    });
  }, []);

  const handleToggleRowVisible = useCallback((siteExecutorId: number) => {
    setUncensoredExecutorIds((prev) => {
      const next = new Set(prev);
      if (next.has(siteExecutorId)) {
        next.delete(siteExecutorId);
      } else {
        next.add(siteExecutorId);
      }
      return next;
    });
  }, []);

  const handleToggleRowExpanded = useCallback((siteExecutorId: number) => {
    setExpandedExecutorIds((prev) => {
      const next = new Set(prev);
      if (next.has(siteExecutorId)) {
        next.delete(siteExecutorId);
      } else {
        next.add(siteExecutorId);
      }
      return next;
    });
  }, []);

  const handleOpenHistory = useCallback(
    (siteExecutorId: number) => {
      onOpenExecutorBalance?.(siteExecutorId);
    },
    [onOpenExecutorBalance]
  );

  const isRowAmountVisible = useCallback(
    (siteExecutorId: number) => allFinancialVisible || uncensoredExecutorIds.has(siteExecutorId),
    [allFinancialVisible, uncensoredExecutorIds]
  );

  const totalBalance = rows.reduce((s, x) => s + (Number(x.balance) || 0), 0);

  return (
    <DashboardCard
      id="accounting-card"
      title="Бухгалтерия"
      icon="ri-calculator-line"
      isCollapsible={true}
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      expandMode="fullscreen"
      isFullscreenOpen={isFullscreenOpen}
      onFullscreenOpenChange={setIsFullscreenOpen}
      headerActions={
        <>
          {hasFinanceAccess && rows.length > 0 && (
            <button
              type="button"
              className={styles.financialCensorGlobalEye}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleAllFinancialVisible();
              }}
              title={
                allFinancialVisible
                  ? 'Скрыть все суммы и итог'
                  : 'Показать все суммы и итог'
              }
              aria-label={allFinancialVisible ? 'Скрыть все суммы' : 'Показать все суммы'}
            >
              <i className={allFinancialVisible ? 'ri-eye-off-line' : 'ri-eye-line'} />
            </button>
          )}
          <button
            type="button"
            className="card-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              loadData();
            }}
            disabled={loading || !hasFinanceAccess}
            title="Обновить"
          >
            <i className={loading ? 'ri-loader-4-line ri-spin' : 'ri-refresh-line'} />
          </button>
        </>
      }
    >
      {!hasFinanceAccess ? (
        <div className={styles.permissionsInfo}>
          <i className="ri-shield-keyhole-line" />
          <span>Нет права «Просмотр финансовых данных».</span>
        </div>
      ) : (
        <>
          {loadError && (
            <div className={styles.errorState}>
              <span>{loadError}</span>
              <button type="button" className={styles.retryButton} onClick={() => loadData()}>
                <i className="ri-refresh-line" />
                Повторить
              </button>
            </div>
          )}

          {loading && rows.length === 0 && !loadError && (
            <div className={styles.emptyState}>
              <i className="ri-loader-4-line ri-spin" />
              <div className={styles.emptyStateTitle}>Загрузка…</div>
            </div>
          )}

          {!loadError && !loading && rows.length === 0 && (
            <div className={styles.emptyState}>
              <i className="ri-team-line" />
              <div className={styles.emptyStateTitle}>Исполнителей нет</div>
            </div>
          )}

          {!loadError && rows.length > 0 && (
            <>
              <div className={styles.summary}>
                <span className={styles.summaryLabel}>Сумма балансов</span>
                <span
                  className={`${styles.summaryValue} ${!allFinancialVisible ? styles.balanceMasked : ''}`}
                >
                  {allFinancialVisible ? formatRub(totalBalance) : MASKED_AMOUNT}
                </span>
              </div>
              <ul className={styles.list}>
                {rows.map((row) => {
                  const b = Number(row.balance) || 0;
                  const sign = b > 0 ? 'plus' : b < 0 ? 'minus' : 'zero';
                  const rowShown = isRowAmountVisible(row.siteExecutorId);
                  const accent = rowShown ? sign : 'zero';
                  const balanceClass =
                    sign === 'plus'
                      ? styles.balancePlus
                      : sign === 'minus'
                        ? styles.balanceMinus
                        : styles.balanceZero;

                  const balanceAmount = (
                    <span className={`${styles.balance} ${balanceClass}`}>{formatRub(b)}</span>
                  );
                  const isExpanded = expandedExecutorIds.has(row.siteExecutorId);

                  return (
                    <li
                      key={row.siteExecutorId}
                      className={`${styles.rowItem} ${isExpanded ? styles.rowItemExpanded : ''}`}
                    >
                      <div
                        className={styles.rowInner}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        title={isExpanded ? 'Свернуть' : 'Действия'}
                        onClick={() => handleToggleRowExpanded(row.siteExecutorId)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleToggleRowExpanded(row.siteExecutorId);
                          }
                        }}
                      >
                        <div className={styles.rowAccent} data-sign={accent} aria-hidden />
                        <div className={styles.rowBody}>
                          <span className={styles.name}>{row.executorDisplayName || 'Исполнитель'}</span>
                          <div className={styles.rowRight}>
                            <div className={styles.balanceToggle}>
                              {allFinancialVisible ? (
                                <div
                                  className={styles.financialBalanceBox}
                                  data-sign={sign}
                                >
                                  {balanceAmount}
                                </div>
                              ) : rowShown ? (
                                <div
                                  className={`${styles.financialBalanceBox} ${styles.financialClickToHide}`}
                                  data-sign={sign}
                                  role="button"
                                  tabIndex={0}
                                  title="Нажмите, чтобы скрыть сумму"
                                  aria-label="Скрыть сумму"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleRowVisible(row.siteExecutorId);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleToggleRowVisible(row.siteExecutorId);
                                    }
                                  }}
                                >
                                  {balanceAmount}
                                </div>
                              ) : (
                                <div className={styles.financialBlurWrapper}>
                                  <div
                                    className={`${styles.financialBalanceBox} ${styles.financialBalanceMasked}`}
                                    data-sign={sign}
                                  >
                                    <span className={`${styles.balance} ${styles.balanceMasked}`}>
                                      {MASKED_AMOUNT}
                                    </span>
                                  </div>
                                  <div
                                    className={styles.financialCensorOverlay}
                                    title="Показать сумму"
                                    role="button"
                                    tabIndex={0}
                                    aria-label="Показать сумму"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleRowVisible(row.siteExecutorId);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleToggleRowVisible(row.siteExecutorId);
                                      }
                                    }}
                                  >
                                    <button
                                      type="button"
                                      className={styles.financialCensorEye}
                                      tabIndex={-1}
                                      aria-hidden
                                    >
                                      <i className="ri-eye-line" />
                                    </button>
                                  </div>
                                </div>
                              )}
                              <i
                                className={`ri-arrow-down-s-line ${styles.balanceChevron} ${isExpanded ? styles.balanceChevronOpen : ''}`}
                                aria-hidden
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      {isExpanded && (row.unpaidByType?.length || 0) > 0 && (
                        <div className={styles.breakdownPanel}>
                          {(row.unpaidByType ?? []).map((t) => (
                            <div key={t.earningTypeId} className={styles.breakdownRow}>
                              <span className={styles.breakdownLabel}>{t.earningTypeName}</span>
                              <span className={styles.breakdownValue}>
                                {rowShown ? formatRub(t.amount) : MASKED_AMOUNT}
                              </span>
                            </div>
                          ))}
                          {Math.abs(row.unallocatedPayments ?? 0) > 0.005 && (
                            <div className={`${styles.breakdownRow} ${styles.breakdownRowMuted}`}>
                              <span className={styles.breakdownLabel}>
                                Выплачено, не распределено
                              </span>
                              <span className={styles.breakdownValue}>
                                {rowShown ? formatRub(row.unallocatedPayments ?? 0) : MASKED_AMOUNT}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      {isExpanded && (
                        <div className={styles.rowActionsPanel}>
                          <button
                            type="button"
                            className={styles.actionBtn}
                            onClick={() => handleOpenHistory(row.siteExecutorId)}
                          >
                            <i className="ri-history-line" />
                            <span>История</span>
                          </button>
                          {canEdit && (
                            <button
                              type="button"
                              className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}
                              onClick={(e) => handleOpenPay(row, e)}
                            >
                              <i className="ri-money-dollar-circle-line" />
                              <span>Оплатить</span>
                            </button>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </>
      )}

      <AccountingPayBottomSheet
        isOpen={paySheetOpen}
        onClose={handleClosePaySheet}
        siteId={siteId}
        target={payTarget}
        onSuccess={loadData}
      />
    </DashboardCard>
  );
};

export default AccountingCard;
